import React from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import FooterSection from "../components/FooterSection";
import PageSEO from "../components/PageSEO";
import "../styles/SeoServicePage.css";

export default function SeoServicePage() {
  return (
    <div className="seo-service-page">
      <PageSEO
        title="SEO Services | Technical SEO, Content & Local SEO | Sparkdenz"
        description="Grow organic traffic with Sparkdenz SEO services including technical SEO, keyword strategy, content optimization, local SEO, and authority building."
        canonicalPath="/seo-service"
      />
      <MenuBar />
      <main>
        {/* Hero Section */}
        <section className="seo-hero">
          <div className="section-inner">
            <div className="seo-hero-content">
              <p className="section-tag">SEO Excellence</p>
              <h1>Dominate Search Rankings and Drive Organic Growth</h1>
              <p className="seo-hero-text">
                Strategic SEO and GEO optimization to capture high-intent customers
                actively searching for your services. We've helped 120+ brands scale
                their organic reach with proven strategies.
              </p>
              <div className="seo-hero-actions">
                <Link className="btn primary" to="/contact-us">
                  Get Your Free SEO Audit
                </Link>
                <Link className="btn ghost" to="/contact-us">
                  Schedule Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="seo-services-grid">
          <div className="section-inner">
            <h2>Our SEO Services</h2>
            <div className="services-grid">
              <div className="service-item">
                <div className="service-icon">🔍</div>
                <h3>Technical SEO</h3>
                <p>
                  Optimize site architecture, speed, crawlability, and indexing.
                  We fix technical issues that prevent search engines from
                  understanding and ranking your content.
                </p>
                <ul className="service-list">
                  <li>Site speed optimization</li>
                  <li>Mobile responsiveness</li>
                  <li>XML sitemap setup</li>
                  <li>Robots.txt optimization</li>
                  <li>Schema markup implementation</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📝</div>
                <h3>Content Strategy</h3>
                <p>
                  Create high-ranking content that answers search intent and
                  converts visitors into leads. Our data-driven approach ensures
                  every piece of content targets real customer demand.
                </p>
                <ul className="service-list">
                  <li>Keyword research & analysis</li>
                  <li>Content gap analysis</li>
                  <li>On-page SEO optimization</li>
                  <li>Blog strategy & execution</li>
                  <li>Long-form content creation</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🗺️</div>
                <h3>Local SEO & GEO Optimization</h3>
                <p>
                  Capture local customers with location-based optimization and
                  Google Business Profile management. Dominate local pack rankings
                  and get more foot traffic to your business.
                </p>
                <ul className="service-list">
                  <li>Google Business Profile optimization</li>
                  <li>Local citation building</li>
                  <li>Local keyword targeting</li>
                  <li>Review management</li>
                  <li>Multi-location optimization</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🔗</div>
                <h3>Link Building & Authority</h3>
                <p>
                  Build authoritative backlinks from relevant, high-quality domains.
                  We focus on quality over quantity to boost your domain authority
                  and search rankings.
                </p>
                <ul className="service-list">
                  <li>Backlink analysis & strategy</li>
                  <li>Competitor link research</li>
                  <li>High-quality link acquisition</li>
                  <li>PR & guest posting</li>
                  <li>Brand mentions tracking</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📊</div>
                <h3>SEO Analytics & Reporting</h3>
                <p>
                  Track what matters with detailed SEO reporting and analytics.
                  We provide monthly insights showing traffic growth, ranking
                  improvements, and lead generation impact.
                </p>
                <ul className="service-list">
                  <li>Monthly performance reports</li>
                  <li>Ranking tracking</li>
                  <li>Traffic analysis</li>
                  <li>Conversion tracking</li>
                  <li>Custom dashboards</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">⚡</div>
                <h3>SEO Audit & Strategy</h3>
                <p>
                  Comprehensive SEO audits identify gaps and opportunities in your
                  current strategy. We create a custom roadmap to increase organic
                  visibility and leads.
                </p>
                <ul className="service-list">
                  <li>Full website SEO audit</li>
                  <li>Competitor analysis</li>
                  <li>Opportunity identification</li>
                  <li>Custom SEO roadmap</li>
                  <li>Quarterly strategy reviews</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="seo-results">
          <div className="section-inner">
            <h2>Real Results From Real Clients</h2>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-stat">245%</div>
                <p>Average increase in organic traffic</p>
              </div>
              <div className="result-card">
                <div className="result-stat">4.5x</div>
                <p>Average ROAS from SEO campaigns</p>
              </div>
              <div className="result-card">
                <div className="result-stat">89%</div>
                <p>Pages improved to first page ranking</p>
              </div>
              <div className="result-card">
                <div className="result-stat">120+</div>
                <p>Successful SEO campaigns</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="seo-process">
          <div className="section-inner">
            <h2>Our SEO Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">01</div>
                <h3>Audit & Discovery</h3>
                <p>We analyze your website, competitors, and target market to identify opportunities and create a strategic foundation.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">02</div>
                <h3>Strategy Development</h3>
                <p>Based on our audit, we create a custom SEO roadmap with clear goals, timelines, and measurable outcomes.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">03</div>
                <h3>Implementation</h3>
                <p>We execute the strategy with technical optimizations, content creation, and link building campaigns.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">04</div>
                <h3>Monitoring & Optimization</h3>
                <p>We continuously track performance and optimize for better results, adapting to algorithm updates and market changes.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="seo-final-cta">
          <div className="section-inner">
            <h2>Ready to Dominate Your Market?</h2>
            <p>Let's build a custom SEO strategy that drives qualified leads and sustainable growth.</p>
            <div className="cta-buttons">
              <Link className="btn primary" to="/contact-us">
                Book Your Free Consultation
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Download SEO Guide
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
